import { useCallback, useEffect, useMemo, useState } from 'react';
import { API_ENDPOINTS, buildApiUrl } from '@/constants/api';

interface UseCallRecordingResult {
    callRecordingUrl: string | null;
    isLoading: boolean;
    errorMessage: string | null;
    handleLoadedData: () => void;
    handleCanPlay: () => void;
    handleAudioError: () => void;
}

const getErrorMessage = (status: number): string => {
    switch (status) {
        case 404:
            return 'No call recording available.';
        case 401:
        case 403:
            return 'Not authorized to access recording.';
        default:
            return 'Failed to load recording.';
    }
};

const useCallRecording = (callId: string | null): UseCallRecordingResult => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const callRecordingUrl = useMemo(() => {
        return callId ? buildApiUrl(API_ENDPOINTS.CALL_RECORDING(callId)) : null;
    }, [callId]);

    useEffect(() => {
        if (!callRecordingUrl) return;

        const controller = new AbortController();

        setIsLoading(true);
        setErrorMessage(null);

        (async () => {
            try {
                const res = await fetch(callRecordingUrl, {
                    method: 'GET',
                    headers: { Range: 'bytes=0-0' },
                    credentials: 'include',
                    signal: controller.signal,
                });

                if (!res.ok) {
                    setErrorMessage(getErrorMessage(res.status));
                    setIsLoading(false);
                }

                // If ok, let audio events clear the loading state
            } catch (e: unknown) {
                if (!(e instanceof Error && e.name === 'AbortError')) {
                    setErrorMessage('Failed to load recording.');
                    setIsLoading(false);
                }
            }
        })();

        return () => controller.abort();
    }, [callRecordingUrl]);

    const handleLoadedData = useCallback(() => {
        setIsLoading(false);
        setErrorMessage(null);
    }, []);

    const handleCanPlay = useCallback(() => {
        setIsLoading(false);
        setErrorMessage(null);
    }, []);

    const handleAudioError = useCallback(() => {
        if (!callRecordingUrl) {
            setErrorMessage('Failed to load recording.');
            setIsLoading(false);
            return;
        }

        void (async () => {
            try {
                const response = await fetch(callRecordingUrl, { credentials: 'include' });
                if (!response.ok) {
                    setErrorMessage(getErrorMessage(response.status));
                } else {
                    setErrorMessage('The audio file could not be played.');
                }
            } catch {
                setErrorMessage('Failed to load recording.');
            } finally {
                setIsLoading(false);
            }
        })();
    }, [callRecordingUrl]);

    return {
        callRecordingUrl,
        isLoading,
        errorMessage,
        handleLoadedData,
        handleCanPlay,
        handleAudioError,
    };
};

export default useCallRecording;
